import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, Users, Download, FileText, Scale, TrendingUp, 
  AlertTriangle, CheckCircle, Fuel, Wallet, Package, Calendar,
  TrendingDown, DollarSign, ShoppingCart, ArrowUpRight, ArrowDownRight, List
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import CustomerListTab from '../components/CustomerListTab';
import CustomerLedgerTab from '../components/CustomerLedgerTab';

// Get current date in IST timezone
const getISTDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Get start of month in IST timezone
const getISTMonthStart = () => {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  istDate.setDate(1);
  return istDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

function Accounts() {
  const [customers, setCustomers] = useState([]);
  const [stationLedger, setStationLedger] = useState({ entries: [], current_balance: 0 });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLedger, setCustomerLedger] = useState(null);
  const [showIdealColumns, setShowIdealColumns] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  
  // Reconciliation state
  const [selectedReconDate, setSelectedReconDate] = useState(getISTDate());
  const [fuelRecon, setFuelRecon] = useState(null);
  const [cashRecon, setCashRecon] = useState(null);
  const [stockRecon, setStockRecon] = useState(null);
  const [creditRecon, setCreditRecon] = useState(null);
  const [salesVariance, setSalesVariance] = useState(null);
  const [salesVarianceHistory, setSalesVarianceHistory] = useState(null);
  const [reconSummary, setReconSummary] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  
  // Period-based reconciliation state
  const [salesVsStockPeriod, setSalesVsStockPeriod] = useState('month');
  const [salesVsStockData, setSalesVsStockData] = useState(null);
  const [salesVsCollectionsPeriod, setSalesVsCollectionsPeriod] = useState('month');
  const [salesVsCollectionsData, setSalesVsCollectionsData] = useState(null);
  
  // P&L state
  const [pnlSummary, setPnlSummary] = useState(null);
  const [pnlTrends, setPnlTrends] = useState([]);
  const [dailyPnl, setDailyPnl] = useState([]);
  const [pnlLoading, setPnlLoading] = useState(true);
  const [pnlViewMode, setPnlViewMode] = useState('summary');
  
  const [dateRange, setDateRange] = useState({
    start: getISTMonthStart(),
    end: getISTDate()
  });

  useEffect(() => {
    fetchData();
    fetchPnlData();
  }, [dateRange]);

  useEffect(() => {
    fetchReconciliationData();
  }, [selectedReconDate]);

  useEffect(() => {
    fetchSalesVsStock();
  }, [salesVsStockPeriod]);

  useEffect(() => {
    fetchSalesVsCollections();
  }, [salesVsCollectionsPeriod]);

  const fetchSalesVsStock = async () => {
    try {
      const res = await axios.get(`${API}/reconciliation/sales-vs-stock?period=${salesVsStockPeriod}`);
      setSalesVsStockData(res.data);
    } catch (error) {
      console.error('Failed to load sales vs stock data');
    }
  };

  const fetchSalesVsCollections = async () => {
    try {
      const res = await axios.get(`${API}/reconciliation/sales-vs-collections?period=${salesVsCollectionsPeriod}`);
      setSalesVsCollectionsData(res.data);
    } catch (error) {
      console.error('Failed to load sales vs collections data');
    }
  };

  const fetchData = async () => {
    try {
      const [cusRes, ledgerRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/station-ledger?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      ]);
      setCustomers(cusRes.data.customers);
      setStationLedger(ledgerRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchCustomerLedger = async (customerId) => {
    try {
      const res = await axios.get(`${API}/customer-ledger/${customerId}?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setCustomerLedger(res.data);
      setSelectedCustomer(customerId);
    } catch (error) {
      toast.error('Failed to load customer ledger');
    }
  };

  // Reconciliation data fetch
  const fetchReconciliationData = async () => {
    setReconLoading(true);
    try {
      const [fuelRes, cashRes, stockRes, creditRes, summaryRes, salesVarRes, salesVarHistRes] = await Promise.all([
        axios.get(`${API}/reconciliation/fuel?date=${selectedReconDate}`),
        axios.get(`${API}/reconciliation/cash?date=${selectedReconDate}`),
        axios.get(`${API}/reconciliation/stock?date=${selectedReconDate}`),
        axios.get(`${API}/reconciliation/customer-credit`),
        axios.get(`${API}/reconciliation/summary?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/reconciliation/sales-variance?date=${selectedReconDate}`),
        axios.get(`${API}/reconciliation/sales-variance-history?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      ]);
      setFuelRecon(fuelRes.data);
      setCashRecon(cashRes.data);
      setStockRecon(stockRes.data);
      setCreditRecon(creditRes.data);
      setReconSummary(summaryRes.data);
      setSalesVariance(salesVarRes.data);
      setSalesVarianceHistory(salesVarHistRes.data);
    } catch (error) {
      console.error('Failed to load reconciliation data');
    } finally {
      setReconLoading(false);
    }
  };

  // P&L data fetch
  const fetchPnlData = async () => {
    setPnlLoading(true);
    try {
      const [summaryRes, trendsRes, dailyRes] = await Promise.all([
        axios.get(`${API}/pnl/summary?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/pnl/trends?months=6`),
        axios.get(`${API}/pnl/daily?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      ]);
      setPnlSummary(summaryRes.data);
      setPnlTrends(trendsRes.data.trends);
      setDailyPnl(dailyRes.data.daily_pnl);
    } catch (error) {
      console.error('Failed to load P&L data');
    } finally {
      setPnlLoading(false);
    }
  };

  const exportStationLedgerPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/station-ledger-pdf?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'station_ledger.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Station ledger downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Sorting functions for Station Ledger
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedEntries = () => {
    if (!stationLedger.entries) return [];
    
    const sortableEntries = [...stationLedger.entries];
    
    // Filter out entries based on toggle state
    const filteredEntries = sortableEntries.filter(entry => {
      // Hide only Totalizer sales (starting with "Daily") when ideal columns are hidden
      if (!showIdealColumns && entry.entry_type === 'sale' && entry.description?.startsWith('Daily')) {
        return false;
      }
      return true;
    });
    
    filteredEntries.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle backward compatibility for old column names
      if (sortConfig.key === 'actual_debit' && !aValue) aValue = a.debit || 0;
      if (sortConfig.key === 'actual_debit' && !bValue) bValue = b.debit || 0;
      if (sortConfig.key === 'actual_credit' && !aValue) aValue = a.credit || 0;
      if (sortConfig.key === 'actual_credit' && !bValue) bValue = b.credit || 0;
      if (sortConfig.key === 'actual_balance' && !aValue) aValue = a.balance || 0;
      if (sortConfig.key === 'actual_balance' && !bValue) bValue = b.balance || 0;
      
      // Handle date sorting
      if (sortConfig.key === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Handle date sorting
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }
      
      return 0;
    });
    
    return filteredEntries;
  };

  const exportCustomerLedgerPDF = async () => {
    if (!selectedCustomer) return;
    try {
      const response = await axios.get(
        `${API}/reports/customer-ledger-pdf/${selectedCustomer}?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer_ledger.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customer ledger downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportReconciliationPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/reconciliation-pdf?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reconciliation_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reconciliation report downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const StatusBadge = ({ status }) => {
    const isOk = status === 'OK';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
        isOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {isOk ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
        {status}
      </span>
    );
  };

  const MetricCard = ({ title, value, subtitle, trend, icon: Icon, color }) => (
    <Card className={`p-4 border-l-4 ${color}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs text-muted font-medium">{title}</p>
          <p className="text-xl font-bold mt-1">{formatCurrency(value)}</p>
          {subtitle && <p className="text-xs text-muted mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color.replace('border-l-', 'bg-').replace('-500', '-100')}`}>
          <Icon className={`w-4 h-4 ${color.replace('border-l-', 'text-')}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>{Math.abs(trend).toFixed(1)}% margin</span>
        </div>
      )}
    </Card>
  );

  const expenseData = pnlSummary?.expenses?.breakdown 
    ? Object.entries(pnlSummary.expenses.breakdown).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="accounts-page">
        {/* Date Range Filter */}
        <Card className="p-3 sm:p-4 border border-border">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm font-medium whitespace-nowrap">From:</label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="flex-1 sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm font-medium whitespace-nowrap">To:</label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="flex-1 sm:w-40"
              />
            </div>
          </div>
        </Card>

        <Tabs defaultValue="station-ledger" className="w-full">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto p-1">
            <TabsTrigger value="station-ledger" data-testid="station-ledger-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <BookOpen className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Ledger
            </TabsTrigger>
            <TabsTrigger value="reconciliation" data-testid="reconciliation-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <Scale className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Reconciliation
            </TabsTrigger>
            <TabsTrigger value="profit-loss" data-testid="profit-loss-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Profit & Loss
            </TabsTrigger>
            <TabsTrigger value="customer-ledger" data-testid="customer-ledger-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Customers
            </TabsTrigger>
          </TabsList>

          {/* CUSTOMERS TAB with Sub-tabs */}
          <TabsContent value="customer-ledger" className="mt-6">
            <div className="space-y-6">
              <h2 className="text-2xl font-heading font-bold">Customers</h2>
              
              <Tabs defaultValue="customer-list" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="customer-list" className="flex items-center gap-2">
                    <List className="w-4 h-4" />
                    Customer List
                  </TabsTrigger>
                  <TabsTrigger value="customer-ledger-detail" className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Customer Ledger
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="customer-list">
                  <CustomerListTab />
                </TabsContent>

                <TabsContent value="customer-ledger-detail">
                  <CustomerLedgerTab dateRange={dateRange} />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          {/* STATION LEDGER TAB */}
          <TabsContent value="station-ledger" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold">Station Ledger</h2>
                <p className="text-muted text-sm">
                  Actual Balance: ₹{stationLedger.current_actual_balance?.toLocaleString('en-IN') || 0} | 
                  Ideal Balance: ₹{stationLedger.current_ideal_balance?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showIdealColumns}
                    onChange={(e) => setShowIdealColumns(e.target.checked)}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Show Ideal Columns</span>
                </label>
                <Button variant="outline" onClick={exportStationLedgerPDF} data-testid="export-station-ledger-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4 border border-border">
                <p className="text-xs text-muted">Total Actual Credits</p>
                <p className="text-xl font-bold text-green-600">₹{stationLedger.total_actual_credit?.toLocaleString('en-IN') || 0}</p>
              </Card>
              <Card className="p-4 border border-border">
                <p className="text-xs text-muted">Total Actual Debits</p>
                <p className="text-xl font-bold text-red-600">₹{stationLedger.total_actual_debit?.toLocaleString('en-IN') || 0}</p>
              </Card>
              <Card className="p-4 border border-border">
                <p className="text-xs text-muted">Net Actual Balance</p>
                <p className={`text-xl font-bold ${(stationLedger.current_actual_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₹{stationLedger.current_actual_balance?.toLocaleString('en-IN') || 0}
                </p>
              </Card>
            </div>

            <Card className="border border-border overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b border-border">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-bold text-muted uppercase cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {sortConfig.key === 'date' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-bold text-muted uppercase cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleSort('entry_type')}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortConfig.key === 'entry_type' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-bold text-muted uppercase cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {sortConfig.key === 'description' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Payment Mode</th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-bold text-muted uppercase cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleSort('actual_debit')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Actual Debit
                        {sortConfig.key === 'actual_debit' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-bold text-muted uppercase cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleSort('actual_credit')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Actual Credit
                        {sortConfig.key === 'actual_credit' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-bold text-muted uppercase bg-blue-50 dark:bg-blue-900/20 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30"
                      onClick={() => handleSort('actual_balance')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Actual Balance
                        {sortConfig.key === 'actual_balance' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    {showIdealColumns && (
                      <>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-muted uppercase bg-green-50 dark:bg-green-900/20 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30"
                          onClick={() => handleSort('ideal_debit')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Ideal Debit
                            {sortConfig.key === 'ideal_debit' && (
                              <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-muted uppercase bg-green-50 dark:bg-green-900/20 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30"
                          onClick={() => handleSort('ideal_credit')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Ideal Credit
                            {sortConfig.key === 'ideal_credit' && (
                              <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-right text-xs font-bold text-muted uppercase bg-green-50 dark:bg-green-900/20 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30"
                          onClick={() => handleSort('ideal_balance')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            Ideal Balance
                            {sortConfig.key === 'ideal_balance' && (
                              <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </div>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {getSortedEntries().map((entry) => {
                    let enhancedDesc = entry.description || '';
                    const details = [];
                    if (entry.customer_name) details.push(entry.customer_name);
                    if (entry.fuel_type) details.push(entry.fuel_type);
                    if (entry.quantity) details.push(`${entry.quantity}L`);
                    if (details.length > 0 && !enhancedDesc.includes(details[0])) {
                      enhancedDesc = `${enhancedDesc} (${details.join(', ')})`;
                    }
                    
                    return (
                      <tr key={entry.id} className="hover:bg-secondary/50">
                        <td className="px-4 py-3 text-sm">{new Date(entry.date).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            entry.entry_type === 'sale' ? 'bg-green-100 text-green-700' :
                            entry.entry_type === 'expense' || entry.entry_type === 'withdrawal' ? 'bg-red-100 text-red-700' :
                            entry.entry_type === 'investment' ? 'bg-emerald-100 text-emerald-700' :
                            entry.entry_type === 'tt_purchase' ? 'bg-blue-100 text-blue-700' :
                            entry.entry_type === 'payment_received' ? 'bg-purple-100 text-purple-700' :
                            entry.entry_type === 'payment_breakdown' ? 'bg-indigo-100 text-indigo-700' :
                            entry.entry_type === 'loss' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {entry.entry_type === 'tt_purchase' ? 'TT Purchase' : 
                             entry.entry_type === 'payment_received' ? 'Payment' :
                             entry.entry_type === 'payment_breakdown' ? 'Payment Breakdown' :
                             entry.entry_type === 'investment' ? 'Investment' :
                             entry.entry_type?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{enhancedDesc}</td>
                        <td className="px-4 py-3 text-sm">
                          {entry.payment_method ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-700 capitalize">
                              {entry.payment_method.replace('_', ' ')}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-red-600">
                          {(entry.actual_debit || entry.debit || 0) > 0 ? `₹${(entry.actual_debit || entry.debit || 0).toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {(entry.actual_credit || entry.credit || 0) > 0 ? `₹${(entry.actual_credit || entry.credit || 0).toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold bg-blue-50 dark:bg-blue-900/20">
                          ₹{(entry.actual_balance || entry.balance || 0).toLocaleString('en-IN')}
                        </td>
                        {showIdealColumns && (
                          <>
                            <td className="px-4 py-3 text-sm text-right text-red-600 bg-green-50 dark:bg-green-900/20">
                              {(entry.ideal_debit || 0) > 0 ? `₹${(entry.ideal_debit || 0).toLocaleString('en-IN')}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-green-600 bg-green-50 dark:bg-green-900/20">
                              {(entry.ideal_credit || 0) > 0 ? `₹${(entry.ideal_credit || 0).toLocaleString('en-IN')}` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold bg-green-50 dark:bg-green-900/20">
                              ₹{(entry.ideal_balance || 0).toLocaleString('en-IN')}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          {/* RECONCILIATION TAB */}
          <TabsContent value="reconciliation" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold">Reconciliation</h2>
                <p className="text-muted text-sm">Compare and validate daily records</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Date:</label>
                  <Input
                    type="date"
                    value={selectedReconDate}
                    onChange={(e) => setSelectedReconDate(e.target.value)}
                    className="w-40"
                    data-testid="recon-date-input"
                  />
                </div>
                <Button variant="outline" onClick={exportReconciliationPDF} data-testid="export-recon-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  Export Report
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            {reconSummary && (
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <Card className="p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <Fuel className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted">Fuel Recon</p>
                      <p className="font-bold">{reconSummary.fuel_reconciliation?.alert_days || 0} Alert Days</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <Wallet className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted">Cash Variance</p>
                      <p className="font-bold">₹{reconSummary.cash_reconciliation?.total_variance?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted">Total Days</p>
                      <p className="font-bold">{reconSummary.fuel_reconciliation?.total_days || 0}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 border border-border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <Users className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted">Credit Over Limit</p>
                      <p className="font-bold">{reconSummary.customer_credit?.over_limit || 0} Customers</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Sales vs Stock Reconciliation - Period Based */}
            <Card className="p-6 border border-border mb-6">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-semibold">Sales vs Stock Reconciliation</h3>
                  <p className="text-sm text-muted">Compare totalizer sales against stock movements</p>
                </div>
                <Select value={salesVsStockPeriod} onValueChange={setSalesVsStockPeriod}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="12month">Last 12 Months</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {salesVsStockData ? (
                <div className="space-y-6">
                  <div className="text-sm text-muted">
                    Period: {salesVsStockData.date_range?.start} to {salesVsStockData.date_range?.end} ({salesVsStockData.date_range?.days} days)
                  </div>

                  <div className="grid md:grid-cols-4 gap-4">
                    <Card className="p-4 border border-border">
                      <p className="text-xs text-muted">Total Sales</p>
                      <p className="text-xl font-bold">{salesVsStockData.summary?.total_sales_qty?.toLocaleString()} L</p>
                      <p className="text-sm text-muted">₹{salesVsStockData.summary?.total_sales_value?.toLocaleString()}</p>
                    </Card>
                    <Card className="p-4 border border-border">
                      <p className="text-xs text-muted">Total Delivered</p>
                      <p className="text-xl font-bold text-green-600">+{salesVsStockData.summary?.total_delivered?.toLocaleString()} L</p>
                      <p className="text-sm text-muted">{salesVsStockData.summary?.deliveries_count} deliveries</p>
                    </Card>
                    <Card className={`p-4 border ${salesVsStockData.summary?.overall_status === 'OK' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20'}`}>
                      <p className="text-xs text-muted">Total Variance</p>
                      <p className={`text-xl font-bold ${salesVsStockData.summary?.total_variance < 0 ? 'text-red-600' : salesVsStockData.summary?.total_variance > 0 ? 'text-green-600' : ''}`}>
                        {salesVsStockData.summary?.total_variance > 0 ? '+' : ''}{salesVsStockData.summary?.total_variance?.toLocaleString()} L
                      </p>
                      <p className="text-sm text-muted">₹{Math.abs(salesVsStockData.summary?.total_variance_value)?.toLocaleString()}</p>
                    </Card>
                    <Card className="p-4 border border-border">
                      <p className="text-xs text-muted">Status</p>
                      <div className="mt-1">
                        <StatusBadge status={salesVsStockData.summary?.overall_status} />
                      </div>
                    </Card>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Card className="p-4 border border-petrol/30 bg-petrol/5">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-semibold text-petrol">MS (Petrol)</h4>
                        <StatusBadge status={salesVsStockData.ms?.status} />
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted">Opening Stock:</span>
                          <span>{salesVsStockData.ms?.opening_stock?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Delivered:</span>
                          <span className="text-green-600">+{salesVsStockData.ms?.delivered?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Sales:</span>
                          <span className="text-red-600">-{salesVsStockData.ms?.sales?.toLocaleString()} L</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between">
                          <span className="text-muted">Expected Closing:</span>
                          <span>{salesVsStockData.ms?.expected_closing?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Actual Closing:</span>
                          <span className="font-medium">{salesVsStockData.ms?.actual_closing?.toLocaleString()} L</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Variance:</span>
                          <span className={salesVsStockData.ms?.variance < 0 ? 'text-red-600' : salesVsStockData.ms?.variance > 0 ? 'text-green-600' : ''}>
                            {salesVsStockData.ms?.variance > 0 ? '+' : ''}{salesVsStockData.ms?.variance?.toLocaleString()} L
                          </span>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 border border-diesel/30 bg-diesel/5">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-semibold text-diesel">HSD (Diesel)</h4>
                        <StatusBadge status={salesVsStockData.hsd?.status} />
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted">Opening Stock:</span>
                          <span>{salesVsStockData.hsd?.opening_stock?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Delivered:</span>
                          <span className="text-green-600">+{salesVsStockData.hsd?.delivered?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Sales:</span>
                          <span className="text-red-600">-{salesVsStockData.hsd?.sales?.toLocaleString()} L</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between">
                          <span className="text-muted">Expected Closing:</span>
                          <span>{salesVsStockData.hsd?.expected_closing?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Actual Closing:</span>
                          <span className="font-medium">{salesVsStockData.hsd?.actual_closing?.toLocaleString()} L</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Variance:</span>
                          <span className={salesVsStockData.hsd?.variance < 0 ? 'text-red-600' : salesVsStockData.hsd?.variance > 0 ? 'text-green-600' : ''}>
                            {salesVsStockData.hsd?.variance > 0 ? '+' : ''}{salesVsStockData.hsd?.variance?.toLocaleString()} L
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              ) : (
                <p className="text-muted text-center py-8">Loading...</p>
              )}
            </Card>

            {/* Sales vs Collections Reconciliation - Period Based */}
            <Card className="p-6 border border-border mb-6">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-semibold">Sales vs Collections Reconciliation</h3>
                  <p className="text-sm text-muted">Compare total sales against actual payment collections</p>
                </div>
                <Select value={salesVsCollectionsPeriod} onValueChange={setSalesVsCollectionsPeriod}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="12month">Last 12 Months</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {salesVsCollectionsData ? (
                <div className="space-y-6">
                  <div className="text-sm text-muted">
                    Period: {salesVsCollectionsData.date_range?.start} to {salesVsCollectionsData.date_range?.end} ({salesVsCollectionsData.date_range?.days} days)
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <Card className="p-4 border border-blue-200 bg-blue-50 dark:bg-blue-900/20">
                      <p className="text-xs text-muted">Total Sales (Meter)</p>
                      <p className="text-2xl font-bold">₹{salesVsCollectionsData.sales?.total_value?.toLocaleString()}</p>
                      <p className="text-sm text-muted">{salesVsCollectionsData.sales?.total_qty?.toLocaleString()} L</p>
                    </Card>
                    <Card className="p-4 border border-green-200 bg-green-50 dark:bg-green-900/20">
                      <p className="text-xs text-muted">Total Collected</p>
                      <p className="text-2xl font-bold text-green-600">₹{salesVsCollectionsData.collections?.total_collected?.toLocaleString()}</p>
                      <p className="text-sm text-muted">{salesVsCollectionsData.reconciliation?.collection_efficiency?.toFixed(1)}% efficiency</p>
                    </Card>
                    <Card className={`p-4 border ${salesVsCollectionsData.reconciliation?.status === 'OK' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs text-muted">Variance</p>
                          <p className={`text-2xl font-bold ${salesVsCollectionsData.reconciliation?.variance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            ₹{Math.abs(salesVsCollectionsData.reconciliation?.variance)?.toLocaleString()}
                          </p>
                          <p className="text-sm text-muted">{salesVsCollectionsData.reconciliation?.variance_percentage?.toFixed(1)}%</p>
                        </div>
                        <StatusBadge status={salesVsCollectionsData.reconciliation?.status} />
                      </div>
                    </Card>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <Card className="p-4 border border-border">
                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                        <Fuel className="w-4 h-4" /> Sales Breakdown
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted">MS (Petrol):</span>
                          <span>{salesVsCollectionsData.sales?.ms_qty?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted pl-4">
                          <span>Value:</span>
                          <span>₹{salesVsCollectionsData.sales?.ms_value?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">HSD (Diesel):</span>
                          <span>{salesVsCollectionsData.sales?.hsd_qty?.toLocaleString()} L</span>
                        </div>
                        <div className="flex justify-between text-xs text-muted pl-4">
                          <span>Value:</span>
                          <span>₹{salesVsCollectionsData.sales?.hsd_value?.toLocaleString()}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Total:</span>
                          <span>₹{salesVsCollectionsData.sales?.total_value?.toLocaleString()}</span>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 border border-border">
                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                        <Wallet className="w-4 h-4" /> Collections Breakdown
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted">Cash:</span>
                          <span>₹{salesVsCollectionsData.collections?.cash?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">UPI (All):</span>
                          <span>₹{salesVsCollectionsData.collections?.upi?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Card:</span>
                          <span>₹{salesVsCollectionsData.collections?.card?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Bank:</span>
                          <span>₹{salesVsCollectionsData.collections?.bank?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Fleet:</span>
                          <span>₹{salesVsCollectionsData.collections?.fleet?.toLocaleString()}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold text-green-600">
                          <span>Total Collected:</span>
                          <span>₹{salesVsCollectionsData.collections?.total_collected?.toLocaleString()}</span>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 border border-border">
                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Credit Summary
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted">Credit Sales Given:</span>
                          <span className="text-amber-600">₹{salesVsCollectionsData.credit?.credit_sales?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Credit Collected:</span>
                          <span className="text-green-600">₹{salesVsCollectionsData.credit?.credit_collection?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted">Ambulance Credit:</span>
                          <span>₹{salesVsCollectionsData.credit?.ambulance_credit?.toLocaleString()}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Net Credit Given:</span>
                          <span className={salesVsCollectionsData.credit?.net_credit_given > 0 ? 'text-amber-600' : 'text-green-600'}>
                            ₹{salesVsCollectionsData.credit?.net_credit_given?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              ) : (
                <p className="text-muted text-center py-8">Loading...</p>
              )}
            </Card>

            {/* Fuel Reconciliation Section */}
            <Card className="p-6 border border-border mb-6">
              <h3 className="text-lg font-semibold mb-4">Daily Fuel Reconciliation</h3>
              <p className="text-sm text-muted mb-6">Compare totalizer readings vs. tank dip readings</p>
              
              {fuelRecon ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* MS Section */}
                  <Card className="p-4 border border-petrol/30 bg-petrol/5">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold text-petrol">MS (Petrol)</h4>
                      <StatusBadge status={fuelRecon.ms?.status} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Previous Stock:</span>
                        <span>{fuelRecon.ms?.prev_stock?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Delivered:</span>
                        <span className="text-green-600">+{fuelRecon.ms?.delivered?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Sales (Totalizer):</span>
                        <span className="text-red-600">-{fuelRecon.ms?.sales_totalizer?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Current Stock:</span>
                        <span>{fuelRecon.ms?.current_stock?.toLocaleString()} L</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between font-semibold">
                        <span>Loss/Gain:</span>
                        <span className={fuelRecon.ms?.loss > 0 ? 'text-red-600' : 'text-green-600'}>
                          {fuelRecon.ms?.loss?.toFixed(2)} L
                        </span>
                      </div>
                    </div>
                  </Card>

                  {/* HSD Section */}
                  <Card className="p-4 border border-diesel/30 bg-diesel/5">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold text-diesel">HSD (Diesel)</h4>
                      <StatusBadge status={fuelRecon.hsd?.status} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Previous Stock:</span>
                        <span>{fuelRecon.hsd?.prev_stock?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Delivered:</span>
                        <span className="text-green-600">+{fuelRecon.hsd?.delivered?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Sales (Totalizer):</span>
                        <span className="text-red-600">-{fuelRecon.hsd?.sales_totalizer?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Current Stock:</span>
                        <span>{fuelRecon.hsd?.current_stock?.toLocaleString()} L</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between font-semibold">
                        <span>Loss/Gain:</span>
                        <span className={fuelRecon.hsd?.loss > 0 ? 'text-red-600' : 'text-green-600'}>
                          {fuelRecon.hsd?.loss?.toFixed(2)} L
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              ) : (
                <p className="text-muted text-center py-8">No data for selected date</p>
              )}
            </Card>

            {/* Customer Credit Alerts */}
            {creditRecon?.alerts?.length > 0 && (
              <Card className="p-6 border border-border mb-6">
                <h3 className="text-lg font-semibold mb-4 text-red-600">Credit Limit Alerts</h3>
                <div className="space-y-2">
                  {creditRecon.alerts.slice(0, 5).map((alert, idx) => (
                    <Card key={idx} className="p-4 border border-red-200 bg-red-50">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <div>
                          <p className="font-medium">{alert.firm_name}</p>
                          <p className="text-sm text-muted">
                            Balance: ₹{alert.current_balance?.toLocaleString()} | 
                            Limit: ₹{alert.credit_limit?.toLocaleString()} | 
                            Over by: ₹{alert.over_by?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}

            {/* Stock vs Sales Analysis */}
            {stockRecon && (
              <Card className="p-6 border border-border">
                <h3 className="text-lg font-semibold mb-4">Stock vs Sales Analysis</h3>
                <p className="text-sm text-muted mb-6">Verify that physical stock changes match sales and deliveries</p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* MS Analysis */}
                  <Card className={`p-4 border ${stockRecon.ms?.status === 'OK' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold">MS (Petrol) Analysis</h4>
                      <StatusBadge status={stockRecon.ms?.status} />
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Purchased (TT)</p>
                          <p className="font-semibold text-blue-600">+{stockRecon.ms?.purchased?.toLocaleString() || 0} L</p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Sales (Totalizer)</p>
                          <p className="font-semibold text-orange-600">-{stockRecon.ms?.sales?.toLocaleString() || 0} L</p>
                        </div>
                      </div>
                      <hr />
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Expected Stock Change</p>
                          <p className="font-semibold">{stockRecon.ms?.expected_stock_change?.toLocaleString() || 0} L</p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Actual Stock Change</p>
                          <p className="font-semibold">{stockRecon.ms?.actual_stock_change?.toLocaleString() || 0} L</p>
                        </div>
                      </div>
                      <div className={`p-3 rounded text-center ${Math.abs(stockRecon.ms?.variance || 0) < 50 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className="text-xs text-muted">Variance (Loss/Gain)</p>
                        <p className={`text-lg font-bold ${(stockRecon.ms?.variance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {stockRecon.ms?.variance?.toFixed(2) || 0} L
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* HSD Analysis */}
                  <Card className={`p-4 border ${stockRecon.hsd?.status === 'OK' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold">HSD (Diesel) Analysis</h4>
                      <StatusBadge status={stockRecon.hsd?.status} />
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Purchased (TT)</p>
                          <p className="font-semibold text-blue-600">+{stockRecon.hsd?.purchased?.toLocaleString() || 0} L</p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Sales (Totalizer)</p>
                          <p className="font-semibold text-orange-600">-{stockRecon.hsd?.sales?.toLocaleString() || 0} L</p>
                        </div>
                      </div>
                      <hr />
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Expected Stock Change</p>
                          <p className="font-semibold">{stockRecon.hsd?.expected_stock_change?.toLocaleString() || 0} L</p>
                        </div>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-xs text-muted">Actual Stock Change</p>
                          <p className="font-semibold">{stockRecon.hsd?.actual_stock_change?.toLocaleString() || 0} L</p>
                        </div>
                      </div>
                      <div className={`p-3 rounded text-center ${Math.abs(stockRecon.hsd?.variance || 0) < 50 ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className="text-xs text-muted">Variance (Loss/Gain)</p>
                        <p className={`text-lg font-bold ${(stockRecon.hsd?.variance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {stockRecon.hsd?.variance?.toFixed(2) || 0} L
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* TT Deliveries for the Day */}
                {stockRecon.tt_deliveries?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">TT Deliveries on Selected Date</h4>
                    <Card className="border border-border overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-border">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-muted uppercase">TT Number</th>
                            <th className="px-4 py-2 text-right text-xs font-bold text-muted uppercase">MS Qty</th>
                            <th className="px-4 py-2 text-right text-xs font-bold text-muted uppercase">HSD Qty</th>
                            <th className="px-4 py-2 text-right text-xs font-bold text-muted uppercase">Total Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {stockRecon.tt_deliveries.map((tt, idx) => (
                            <tr key={idx} className="hover:bg-secondary/50">
                              <td className="px-4 py-2 text-sm font-medium">{tt.tt_number || 'N/A'}</td>
                              <td className="px-4 py-2 text-sm text-right">{tt.ms_quantity?.toLocaleString() || 0} L</td>
                              <td className="px-4 py-2 text-sm text-right">{tt.hsd_quantity?.toLocaleString() || 0} L</td>
                              <td className="px-4 py-2 text-sm text-right font-semibold">
                                ₹{((tt.ms_total_amount || 0) + (tt.hsd_total_amount || 0)).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                )}
              </Card>
            )}

            {/* Sales Variance Reconciliation */}
            {salesVariance && (
              <Card className="p-6 border border-border mt-6">
                <h3 className="text-lg font-semibold mb-4">Totalizer vs Recorded Customer Sales</h3>
                <p className="text-sm text-muted mb-6">Compare totalizer-based sales with recorded customer transactions</p>
                
                {/* Summary */}
                <div className="flex justify-between items-center p-4 rounded-lg bg-secondary/50 mb-6">
                  <div>
                    <p className="text-sm text-muted">Overall Status</p>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={salesVariance.summary?.overall_status} />
                      <span className="text-xs text-muted">
                        {salesVariance.summary?.unrecorded_sales_note}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted">Total Unrecorded Sales</p>
                    <p className={`text-xl font-bold ${salesVariance.summary?.total_variance_qty > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {salesVariance.summary?.total_variance_qty?.toLocaleString()} L
                    </p>
                    <p className="text-sm text-muted">
                      ₹{salesVariance.summary?.total_variance_value?.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* MS Sales Variance */}
                  <Card className="p-4 border border-amber-200 bg-amber-50/30">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold text-amber-700">MS (Petrol)</h4>
                      <StatusBadge status={salesVariance.ms?.status} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Totalizer Sales:</span>
                        <span>{salesVariance.ms?.totalizer_sales_qty?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Customer Sales Recorded:</span>
                        <span>{salesVariance.ms?.recorded_customer_sales_qty?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Other Transactions:</span>
                        <span>{salesVariance.ms?.other_transactions_qty?.toLocaleString()} L</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between font-medium">
                        <span>Total Recorded:</span>
                        <span>{salesVariance.ms?.total_recorded_qty?.toLocaleString()} L</span>
                      </div>
                      <div className={`flex justify-between font-bold p-2 rounded ${salesVariance.ms?.quantity_variance > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                        <span>Unrecorded (Walk-in Cash):</span>
                        <span className={salesVariance.ms?.quantity_variance > 0 ? 'text-amber-700' : 'text-green-700'}>
                          {salesVariance.ms?.quantity_variance > 0 ? '+' : ''}{salesVariance.ms?.quantity_variance?.toLocaleString()} L
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-muted">
                        <span>Variance %:</span>
                        <span>{salesVariance.ms?.variance_percentage}%</span>
                      </div>
                    </div>
                  </Card>

                  {/* HSD Sales Variance */}
                  <Card className="p-4 border border-blue-200 bg-blue-50/30">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold text-blue-700">HSD (Diesel)</h4>
                      <StatusBadge status={salesVariance.hsd?.status} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted">Totalizer Sales:</span>
                        <span>{salesVariance.hsd?.totalizer_sales_qty?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Customer Sales Recorded:</span>
                        <span>{salesVariance.hsd?.recorded_customer_sales_qty?.toLocaleString()} L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted">Other Transactions:</span>
                        <span>{salesVariance.hsd?.other_transactions_qty?.toLocaleString()} L</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between font-medium">
                        <span>Total Recorded:</span>
                        <span>{salesVariance.hsd?.total_recorded_qty?.toLocaleString()} L</span>
                      </div>
                      <div className={`flex justify-between font-bold p-2 rounded ${salesVariance.hsd?.quantity_variance > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                        <span>Unrecorded (Walk-in Cash):</span>
                        <span className={salesVariance.hsd?.quantity_variance > 0 ? 'text-amber-700' : 'text-green-700'}>
                          {salesVariance.hsd?.quantity_variance > 0 ? '+' : ''}{salesVariance.hsd?.quantity_variance?.toLocaleString()} L
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-muted">
                        <span>Variance %:</span>
                        <span>{salesVariance.hsd?.variance_percentage}%</span>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Sales Variance History */}
                {salesVarianceHistory?.history?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Variance Trend ({salesVarianceHistory.period.start} to {salesVarianceHistory.period.end})</h4>
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      <Card className="p-3 border border-border">
                        <p className="text-xs text-muted">Days Analyzed</p>
                        <p className="text-lg font-bold">{salesVarianceHistory.total_days}</p>
                      </Card>
                      <Card className="p-3 border border-amber-200 bg-amber-50">
                        <p className="text-xs text-muted">Variance Days</p>
                        <p className="text-lg font-bold text-amber-600">{salesVarianceHistory.variance_days}</p>
                      </Card>
                      <Card className="p-3 border border-border">
                        <p className="text-xs text-muted">Unrecorded Qty</p>
                        <p className="text-lg font-bold">{salesVarianceHistory.totals?.total_variance_qty?.toLocaleString()} L</p>
                      </Card>
                      <Card className="p-3 border border-border">
                        <p className="text-xs text-muted">Unrecorded Value</p>
                        <p className="text-lg font-bold">₹{salesVarianceHistory.totals?.total_variance_value?.toLocaleString()}</p>
                      </Card>
                    </div>
                    <Card className="border border-border overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-border sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-bold text-muted uppercase">Date</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">MS Total</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">MS Rec</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">MS Var</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">HSD Total</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">HSD Rec</th>
                            <th className="px-3 py-2 text-right text-xs font-bold text-muted uppercase">HSD Var</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border text-xs">
                          {salesVarianceHistory.history.map((row, idx) => (
                            <tr key={idx} className={row.has_variance ? 'bg-amber-50' : ''}>
                              <td className="px-3 py-2">{row.date}</td>
                              <td className="px-3 py-2 text-right">{row.ms_totalizer?.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{row.ms_recorded?.toLocaleString()}</td>
                              <td className={`px-3 py-2 text-right font-medium ${row.ms_variance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {row.ms_variance > 0 ? '+' : ''}{row.ms_variance?.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right">{row.hsd_totalizer?.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{row.hsd_recorded?.toLocaleString()}</td>
                              <td className={`px-3 py-2 text-right font-medium ${row.hsd_variance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {row.hsd_variance > 0 ? '+' : ''}{row.hsd_variance?.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                )}
              </Card>
            )}
          </TabsContent>

          {/* PROFIT & LOSS TAB */}
          <TabsContent value="profit-loss" className="mt-6">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold">Profit & Loss</h2>
                <p className="text-muted text-sm">Financial performance overview</p>
              </div>
              <Select value={pnlViewMode} onValueChange={setPnlViewMode}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="trends">Trends</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {pnlLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* Summary View */}
                {pnlViewMode === 'summary' && pnlSummary && (
                  <>
                    {/* Data Discrepancy Warning */}
                    {pnlSummary.losses?.has_data_discrepancy && (
                      <Card className="p-4 border-2 border-orange-300 bg-orange-50 mb-6">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-6 h-6 text-orange-600" />
                          <div>
                            <p className="font-semibold text-orange-800">Stock Data Discrepancy Detected</p>
                            <p className="text-sm text-orange-700">
                              The recorded fuel loss ({pnlSummary.losses?.raw_ms_loss}L MS, {pnlSummary.losses?.raw_hsd_loss}L HSD) is unusually high. 
                              This may indicate incorrect stock readings or initialization issues. P&L calculations have been adjusted to use reasonable loss estimates.
                            </p>
                          </div>
                        </div>
                      </Card>
                    )}

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <MetricCard
                        title="Total Revenue"
                        value={pnlSummary.revenue?.total_revenue}
                        subtitle={`MS: ${pnlSummary.revenue?.ms_sales_qty?.toLocaleString() || 0}L | HSD: ${pnlSummary.revenue?.hsd_sales_qty?.toLocaleString() || 0}L`}
                        icon={DollarSign}
                        color="border-l-green-500"
                      />
                      <MetricCard
                        title="Cost of Goods"
                        value={pnlSummary.cost_of_goods?.total_cogs}
                        subtitle={`MS: ₹${pnlSummary.cost_of_goods?.ms_avg_cost}/L | HSD: ₹${pnlSummary.cost_of_goods?.hsd_avg_cost}/L`}
                        icon={ShoppingCart}
                        color="border-l-blue-500"
                      />
                      <MetricCard
                        title="Gross Profit"
                        value={pnlSummary.gross_profit?.amount}
                        trend={pnlSummary.gross_profit?.margin_percentage}
                        icon={TrendingUp}
                        color="border-l-emerald-500"
                      />
                      <MetricCard
                        title="Net Profit"
                        value={pnlSummary.net_profit?.amount}
                        trend={pnlSummary.net_profit?.margin_percentage}
                        icon={pnlSummary.net_profit?.amount >= 0 ? TrendingUp : TrendingDown}
                        color={pnlSummary.net_profit?.amount >= 0 ? 'border-l-green-500' : 'border-l-red-500'}
                      />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6">
                      {/* Income Statement */}
                      <Card className="p-6 border border-border">
                        <h3 className="text-lg font-semibold mb-4">Income Statement</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b border-border">
                            <span className="font-medium">Revenue</span>
                            <span className="font-bold text-green-600">{formatCurrency(pnlSummary.revenue?.total_revenue)}</span>
                          </div>
                          <div className="flex justify-between text-sm pl-4">
                            <span className="text-muted">MS (Petrol) - {pnlSummary.revenue?.ms_sales_qty?.toLocaleString()}L @ ₹{pnlSummary.revenue?.ms_sell_price}</span>
                            <span>{formatCurrency(pnlSummary.revenue?.ms_revenue)}</span>
                          </div>
                          <div className="flex justify-between text-sm pl-4">
                            <span className="text-muted">HSD (Diesel) - {pnlSummary.revenue?.hsd_sales_qty?.toLocaleString()}L @ ₹{pnlSummary.revenue?.hsd_sell_price}</span>
                            <span>{formatCurrency(pnlSummary.revenue?.hsd_revenue)}</span>
                          </div>
                          
                          <div className="flex justify-between py-2 border-b border-border">
                            <span className="font-medium">Cost of Goods Sold</span>
                            <span className="font-bold text-red-600">({formatCurrency(pnlSummary.cost_of_goods?.total_cogs)})</span>
                          </div>
                          
                          <div className="flex justify-between py-2 bg-green-50 dark:bg-green-900/20 px-3 rounded">
                            <span className="font-semibold">Gross Profit</span>
                            <span className="font-bold text-green-600">{formatCurrency(pnlSummary.gross_profit?.amount)}</span>
                          </div>
                          
                          <div className="flex justify-between py-2 border-b border-border">
                            <span className="font-medium">Operating Expenses</span>
                            <span className="font-bold text-red-600">({formatCurrency(pnlSummary.expenses?.total)})</span>
                          </div>
                          
                          <div className="flex justify-between py-2 border-b border-border">
                            <span className="font-medium">Losses (Fuel)</span>
                            <span className="font-bold text-red-600">({formatCurrency(pnlSummary.losses?.total_value)})</span>
                          </div>
                          
                          <div className={`flex justify-between py-3 px-3 rounded ${pnlSummary.net_profit?.amount >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                            <span className="font-bold">Net Profit</span>
                            <span className={`font-bold text-lg ${pnlSummary.net_profit?.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(pnlSummary.net_profit?.amount)}
                            </span>
                          </div>
                        </div>
                      </Card>

                      {/* Expense Breakdown */}
                      <Card className="p-6 border border-border">
                        <h3 className="text-lg font-semibold mb-4">Expense Breakdown</h3>
                        {expenseData.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={200}>
                              <PieChart>
                                <Pie
                                  data={expenseData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={80}
                                  paddingAngle={2}
                                  dataKey="value"
                                >
                                  {expenseData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="grid grid-cols-2 gap-2 mt-4">
                              {expenseData.slice(0, 6).map((item, index) => (
                                <div key={item.name} className="flex items-center gap-2 text-sm">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                  <span className="truncate">{item.name}</span>
                                  <span className="ml-auto font-medium">{formatCurrency(item.value)}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-center h-48 text-muted">
                            No expense data for this period
                          </div>
                        )}
                      </Card>
                    </div>
                  </>
                )}

                {/* Trends View */}
                {pnlViewMode === 'trends' && (
                  <Card className="p-6 border border-border">
                    <h3 className="text-lg font-semibold mb-4">6-Month Profit Trend</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={pnlTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month_name" />
                        <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="revenue" name="Revenue" fill="#10B981" />
                        <Bar dataKey="cogs" name="COGS" fill="#3B82F6" />
                        <Bar dataKey="expenses" name="Expenses" fill="#F59E0B" />
                        <Bar dataKey="net_profit" name="Net Profit" fill="#8B5CF6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}

                {/* Daily View */}
                {pnlViewMode === 'daily' && (
                  <>
                    <Card className="p-6 border border-border mb-6">
                      <h3 className="text-lg font-semibold mb-4">Daily Net Profit</h3>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={dailyPnl}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                          <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2} />
                          <Line type="monotone" dataKey="net_profit" name="Net Profit" stroke="#8B5CF6" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>

                    <Card className="border border-border overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-border">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Date</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">MS Sales</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">HSD Sales</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">COGS</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Expenses</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Net Profit</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {dailyPnl.map((day) => (
                            <tr key={day.date} className="hover:bg-secondary/50">
                              <td className="px-4 py-3 text-sm">{new Date(day.date).toLocaleDateString('en-IN')}</td>
                              <td className="px-4 py-3 text-sm text-right">{day.ms_sales?.toLocaleString()} L</td>
                              <td className="px-4 py-3 text-sm text-right">{day.hsd_sales?.toLocaleString()} L</td>
                              <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(day.revenue)}</td>
                              <td className="px-4 py-3 text-sm text-right text-blue-600">{formatCurrency(day.cogs)}</td>
                              <td className="px-4 py-3 text-sm text-right text-orange-600">{formatCurrency(day.expenses)}</td>
                              <td className={`px-4 py-3 text-sm text-right font-semibold ${day.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(day.net_profit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default Accounts;
